<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.ADMINS.CREATE_OR_UPDATE.SHOW"
    :handler="(admin) => openEditor(admin)"
  />

  <DialogComponent ref="modal" type="modal" @closed="onClosed">
    <div class="w-full h-full flex flex-col overflow-auto bg-slate-50">
      <!-- HEADER -->
      <div class="flex justify-between items-center p-6 border-b bg-white sticky top-0 z-2">
        <h2 class="text-xl font-semibold text-gray-900">
          {{ isEditMode ? 'Modifier' : 'Ajouter' }} un administrateur
        </h2>
        <button
          class="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
          @click="close"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- CONTENT -->
      <div class="flex-1 overflow-auto p-6 space-y-6">
        <!-- BASIC INFO -->
        <div class="bg-white rounded-xl p-4">
          <h3 class="font-medium text-gray-900 mb-4">Informations personnelles</h3>
          <div class="grid grid-cols-2 gap-4">
            <!-- FIRST NAME -->
            <div>
              <label class="block mb-2 text-sm font-medium text-gray-700">Prénom *</label>
              <input
                v-model="form.firstName"
                type="text"
                placeholder="Jean"
                class="inputComponent w-full"
              />
            </div>

            <!-- LAST NAME -->
            <div>
              <label class="block mb-2 text-sm font-medium text-gray-700">Nom *</label>
              <input
                v-model="form.lastName"
                type="text"
                placeholder="Doe"
                class="inputComponent w-full"
              />
            </div>
          </div>

          <!-- EMAIL -->
          <div class="mt-4">
            <label class="block mb-2 text-sm font-medium text-gray-700">Email *</label>
            <input
              v-model="form.email"
              type="email"
              placeholder="admin@exemple.com"
              class="inputComponent w-full"
              :disabled="isEditMode"
            />
            <p v-if="isEditMode" class="text-xs text-gray-500 mt-1">L'email ne peut pas être modifié</p>
          </div>

          <!-- PASSWORD (only for create) -->
          <div v-if="!isEditMode" class="mt-4">
            <label class="block mb-2 text-sm font-medium text-gray-700">Mot de passe *</label>
            <input
              v-model="form.password"
              type="password"
              placeholder="********"
              class="inputComponent w-full"
            />
            <p class="text-xs text-gray-500 mt-1">Ce mot de passe sera temporaire, l'admin devra le changer</p>
          </div>
        </div>

        <!-- FEATURES -->
        <div class="bg-white rounded-xl p-4">
          <h3 class="font-medium text-gray-900 mb-4">Fonctionnalités autorisées</h3>
          <div class="grid grid-cols-2 gap-3">
            <label
              v-for="(label, key) in $constants.ADMIN.AVAILABLE_FEATURES"
              :key="key"
              class="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
              :class="form.features.includes(key) ? 'border-primary-500 bg-primary-50' : 'border-gray-200'"
            >
              <input
                v-model="form.features"
                type="checkbox"
                :value="key"
                class="w-5 h-5 text-primary-600 rounded"
              />
              <span class="text-sm">{{ label }}</span>
            </label>
          </div>
        </div>

        <!-- COUNTRIES -->
        <div class="bg-white rounded-xl p-4">
          <h3 class="font-medium text-gray-900 mb-4">Pays autorisés</h3>
          <div class="grid grid-cols-2 gap-3">
            <label
              v-for="(name, code) in $constants.ADMIN.AVAILABLE_COUNTRIES"
              :key="code"
              class="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
              :class="form.countries.includes(code) ? 'border-primary-500 bg-primary-50' : 'border-gray-200'"
            >
              <input
                v-model="form.countries"
                type="checkbox"
                :value="code"
                class="w-5 h-5 text-primary-600 rounded"
              />
              <span class="text-sm">{{ name }}</span>
            </label>
          </div>
        </div>
      </div>

      <!-- FOOTER -->
      <div class="flex justify-end gap-3 p-6 border-t bg-white">
        <button
          class="px-6 py-2 rounded-lg bg-gray-200 text-gray-700 font-medium hover:bg-gray-300"
          @click="close"
        >
          Annuler
        </button>
        <button
          class="px-6 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700"
          @click="save"
          :disabled="saving"
        >
          {{ saving ? 'En cours...' : (isEditMode ? 'Mettre à jour' : 'Créer') }}
        </button>
      </div>
    </div>
  </DialogComponent>
</template>

<script>
export default {
  name: 'AdminEditor',

  data() {
    return {
      modal: null,
      isEditMode: false,
      saving: false,
      currentAdmin: null,
      form: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        features: [],
        countries: []
      }
    };
  },

  mounted() {
    this.modal = this.$refs.modal;

    // Listen for create/update events
    this.$bus.on(this.$constants.EVENTS.ADMINS.CREATE_OR_UPDATE.CREATED, () => {
      this.saving = false;
      this.close();
    });

    this.$bus.on(this.$constants.EVENTS.ADMINS.CREATE_OR_UPDATE.UPDATED, () => {
      this.saving = false;
      this.close();
    });

    this.$bus.on(this.$constants.EVENTS.ADMINS.CREATE_OR_UPDATE.CREATE_ERROR, (error) => {
      this.saving = false;
      if (error?.code !== 'EMAIL_EXISTS') {
        // Email exists error is already handled in controller
        // Show other errors here if needed
      }
    });

    this.$bus.on(this.$constants.EVENTS.ADMINS.CREATE_OR_UPDATE.UPDATE_ERROR, () => {
      this.saving = false;
    });
  },

  beforeUnmount() {
    this.$bus.off(this.$constants.EVENTS.ADMINS.CREATE_OR_UPDATE.CREATED);
    this.$bus.off(this.$constants.EVENTS.ADMINS.CREATE_OR_UPDATE.UPDATED);
    this.$bus.off(this.$constants.EVENTS.ADMINS.CREATE_OR_UPDATE.CREATE_ERROR);
    this.$bus.off(this.$constants.EVENTS.ADMINS.CREATE_OR_UPDATE.UPDATE_ERROR);
  },

  methods: {
    openEditor(admin) {
      this.isEditMode = !!admin;
      this.currentAdmin = admin;

      if (admin) {
        // Edit mode
        this.form = {
          firstName: admin.firstName || '',
          lastName: admin.lastName || '',
          email: admin.email || '',
          password: '',
          features: admin.metadata?.features?.slice() || [],
          countries: admin.metadata?.countries?.slice() || []
        };
      } else {
        // Create mode
        this.form = {
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          features: Object.keys(this.$constants.ADMIN.AVAILABLE_FEATURES),
          countries: Object.keys(this.$constants.ADMIN.AVAILABLE_COUNTRIES)
        };
      }

      this.modal?.show();
    },

    close() {
      this.modal?.hide();
    },

    onClosed() {
      this.currentAdmin = null;
      this.isEditMode = false;
      this.saving = false;
    },

    save() {
      // Validation
      if (!this.form.firstName.trim()) {
        alert('Veuillez renseigner le prénom');
        return;
      }
      if (!this.form.lastName.trim()) {
        alert('Veuillez renseigner le nom');
        return;
      }
      if (!this.form.email.trim()) {
        alert('Veuillez renseigner l\'email');
        return;
      }
      if (!this.isEditMode && !this.form.password.trim()) {
        alert('Veuillez renseigner le mot de passe');
        return;
      }
      if (this.form.features.length === 0) {
        alert('Veuillez sélectionner au moins une fonctionnalité');
        return;
      }
      if (this.form.countries.length === 0) {
        alert('Veuillez sélectionner au moins un pays');
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.form.email)) {
        alert('Veuillez renseigner un email valide');
        return;
      }

      this.saving = true;

      if (this.isEditMode) {
        // Update
        const updateData = {
          id: this.currentAdmin.id,
          firstName: this.form.firstName.trim(),
          lastName: this.form.lastName.trim(),
          features: this.form.features,
          countries: this.form.countries
        };
        this.$bus.emit(this.$constants.EVENTS.ADMINS.CREATE_OR_UPDATE.UPDATING, updateData);
      } else {
        // Create
        const createData = {
          firstName: this.form.firstName.trim(),
          lastName: this.form.lastName.trim(),
          email: this.form.email.trim(),
          password: this.form.password,
          features: this.form.features,
          countries: this.form.countries
        };
        this.$bus.emit(this.$constants.EVENTS.ADMINS.CREATE_OR_UPDATE.CREATING, createData);
      }
    }
  }
};
</script>
